import { Product, ProductCategory } from "@/types/product";
import { supabase } from "@/integrations/supabase/client";

// Constants for anti-scraping pagination
const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 100;

export interface PaginatedProducts {
  products: Product[];
  hasMore: boolean;
  total: number;
  page: number;
  pageSize: number;
}

export const productService = {
  /**
   * Get featured product IDs for priority sorting
   */
  getFeaturedProductIds: async (): Promise<Set<string>> => {
    const { data } = await supabase
      .from("featured_products")
      .select("product_id")
      .eq("is_active", true);
    
    return new Set(data?.map(f => f.product_id) || []);
  },

  /**
   * Get all products with mandatory pagination (anti-scraping)
   */
  getAll: async (page = 1, pageSize = DEFAULT_PAGE_SIZE): Promise<PaginatedProducts> => {
    // Enforce maximum page size to prevent data harvesting
    const safePageSize = Math.min(Math.max(1, pageSize), MAX_PAGE_SIZE);
    const safePage = Math.max(1, page);
    const from = (safePage - 1) * safePageSize;
    const to = from + safePageSize - 1;

    const { data, error, count } = await supabase
      .from("products")
      .select(`
        *,
        categories (
          id,
          name,
          slug
        )
      `, { count: 'exact' })
      .eq("is_active", true)
      .order("name", { ascending: true })
      .range(from, to);

    if (error) throw error;
    
    return {
      products: data as Product[],
      hasMore: (count || 0) > to + 1,
      total: count || 0,
      page: safePage,
      pageSize: safePageSize
    };
  },

  /**
   * Legacy method for backward compatibility - fetches first page only
   * @deprecated Use getAll with pagination parameters instead
   */
  getAllLegacy: async (): Promise<Product[]> => {
    const result = await productService.getAll(1, MAX_PAGE_SIZE);
    return result.products;
  },

  getById: async (slug: string): Promise<Product | null> => {
    const { data, error } = await supabase
      .from("products")
      .select(`
        *,
        categories (
          id,
          name,
          slug
        )
      `)
      .eq("slug", slug)
      .eq("is_active", true)
      .maybeSingle();

    if (error) throw error;
    return data as Product | null;
  },

  getByCategory: async (categorySlug: string): Promise<Product[]> => {
    const { data: category } = await supabase
      .from("categories")
      .select("id")
      .eq("slug", categorySlug)
      .single();

    if (!category) return [];

    const { data, error } = await supabase
      .from("products")
      .select(`
        *,
        categories (
          id,
          name,
          slug
        )
      `)
      .eq("category_id", category.id)
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (error) throw error;
    return data as Product[];
  },

  getCategories: async (): Promise<ProductCategory[]> => {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq("is_active", true)
      .order("display_order", { ascending: true });

    if (error) throw error;
    return data as ProductCategory[];
  },
};
